import { Button, Checkbox, NumberInput, Search, Tag } from '@carbon/react';
import {
  asProblem,
  DateField,
  EmptyState,
  FormModal,
  isEmptyValue,
  problemMessage,
  requiredMarking,
  useMarkedLabel,
} from '@pos/admin-ui';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { ListLoading } from '../common/Loading';
import { ErrorNotice } from '../common/Notices';
import { today, unitKey, type Candidate, type Kind, type PriceTarget, type PriceUnit } from './priceDraft';

const PAGE = 50;

/**
 * Picks several items (options included) or deals of the price list's brand at once, with a price and start date for all of
 * them (both can be changed row by row afterwards). What is already in the table can't be picked again.
 */
export function AddPricesDialog({
  priceListId,
  kind,
  inTable,
  onAdd,
  onClose,
}: {
  priceListId: string;
  kind: Kind;
  /** Targets already in the table, saved or not, as `unitKey(target, unit)`. */
  inTable: ReadonlySet<string>;
  onAdd(targets: PriceTarget[], defaults: { price: string; from: string }): void;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [size, setSize] = useState(PAGE);
  const [chosen, setChosen] = useState<Map<string, PriceTarget>>(new Map());
  const [price, setPrice] = useState('');
  const [from, setFrom] = useState(today);
  const candidates = useQuery({
    queryKey: ['ho', 'price-candidates', priceListId, kind, search, size],
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/menu/price-lists/{id}/candidates', {
        params: { path: { id: priceListId }, query: { kind, search: search || undefined, page: 1, pageSize: size } },
      });
      if (!data) throw error;
      return data;
    },
    placeholderData: keepPreviousData,
  });
  const items = candidates.data?.items ?? [];
  const total = Number(candidates.data?.total ?? 0);
  const label = (key: string) => t(`priceTable.${key}.${kind}` as I18nKey);

  // An item is priced per unit: each of its units is one choice; a deal is one choice.
  const choices = (c: Candidate): { key: string; target: PriceTarget; unit?: PriceUnit; already: boolean }[] =>
    kind === 'item' && c.units.length > 0
      ? c.units.map((unit) => ({
          key: unitKey(c.id, unit.uomId),
          target: { ...c, unit },
          unit,
          already: unit.inList || inTable.has(unitKey(c.id, unit.uomId)),
        }))
      : [{ key: unitKey(c.id), target: c, already: c.inList || inTable.has(unitKey(c.id)) }];
  const toggle = (key: string, target: PriceTarget, on: boolean) =>
    setChosen((current) => {
      const next = new Map(current);
      if (on) next.set(key, target);
      else next.delete(key);
      return next;
    });
  const selectable = items.flatMap(choices).filter((c) => !c.already);

  // One of the two fields is required (the start date): marked as the minority rule says.
  const marking = requiredMarking([{ required: false }, { required: true }]);
  const [submitted, setSubmitted] = useState(false);
  const mark = useMarkedLabel();
  const fromMissing = submitted && isEmptyValue(from);

  return (
    <FormModal
      size="lg"
      title={label('add')}
      primaryText={t('priceTable.addChosen', { count: chosen.size })}
      disabled={chosen.size === 0}
      pending={false}
      onSubmit={() => {
        setSubmitted(true);
        if (isEmptyValue(from)) return;
        onAdd([...chosen.values()], { price, from });
        onClose();
      }}
      onClose={onClose}
    >
      <div className="pos-price-picker">
        <Search
          labelText={t('admin.search')}
          placeholder={t('priceTable.searchCatalog')}
          closeButtonLabelText={t('carbon.clearSearch')}
          value={search}
          // Enter searches; it must not submit the dialog.
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
          onChange={(e) => {
            setSearch(e.target.value);
            setSize(PAGE);
          }}
        />
        {candidates.error && (
          <ErrorNotice
            title={problemMessage(t, asProblem(candidates.error))}
            onRetry={() => void candidates.refetch()}
          />
        )}
        <div className="pos-price-picker__bar">
          <Checkbox
            id="price-picker-all"
            labelText={t('priceTable.selectShown', { count: selectable.length })}
            checked={selectable.length > 0 && selectable.every((c) => chosen.has(c.key))}
            disabled={selectable.length === 0}
            onChange={(_, { checked }) => selectable.forEach((c) => toggle(c.key, c.target, checked))}
          />
          <span className="pos-price-picker__count">{t('priceTable.chosen', { count: chosen.size })}</span>
        </div>
        {candidates.isLoading && <ListLoading />}
        {items.length > 0 && (
          <ul className="pos-price-picker__list" aria-label={label('title')}>
            {items.map((c) => {
              const name = c.groupName ? `${c.groupName} › ${c.name}` : c.name;
              const options = choices(c);
              // A deal has no units: one checkbox for it. An item shows its units under it.
              if (!options[0]?.unit) {
                const option = options[0];
                return (
                  <li key={c.id} className="pos-price-picker__row">
                    <Checkbox
                      id={`price-pick-${c.id}`}
                      labelText={
                        <span className="pos-price-picker__label">
                          <span className="pos-price-picker__code">{c.code}</span>
                          <span>{name}</span>
                        </span>
                      }
                      checked={Boolean(option?.already) || chosen.has(option?.key ?? '')}
                      disabled={option?.already}
                      onChange={(_, { checked }) => option && toggle(option.key, option.target, checked)}
                    />
                    {option?.already && <Tag type="gray">{t('priceTable.inList')}</Tag>}
                    {!c.isActive && <Tag type="gray">{t('priceTable.inactive')}</Tag>}
                  </li>
                );
              }
              return (
                <li key={c.id} className="pos-price-picker__item">
                  <div className="pos-price-picker__label">
                    <span className="pos-price-picker__code">{c.code}</span>
                    <span>{name}</span>
                    {!c.isActive && <Tag type="gray">{t('priceTable.inactive')}</Tag>}
                  </div>
                  <ul className="pos-price-picker__units" aria-label={t('priceTable.unitsOf', { name: c.name })}>
                    {options.map((o) => (
                      <li key={o.key} className="pos-price-picker__row">
                        <Checkbox
                          id={`price-pick-${o.key}`}
                          labelText={
                            <span className="pos-price-picker__label">
                              <span className="pos-price-picker__code">{o.unit?.code}</span>
                              <span>{o.unit?.name}</span>
                            </span>
                          }
                          checked={o.already || chosen.has(o.key)}
                          disabled={o.already}
                          onChange={(_, { checked }) => toggle(o.key, o.target, checked)}
                        />
                        {o.unit?.isBase ? (
                          <Tag type="gray">{t('priceTable.baseUnit')}</Tag>
                        ) : (
                          <Tag type="gray">{t('priceTable.unitSize', { factor: Number(o.unit?.factor) })}</Tag>
                        )}
                        {o.already && <Tag type="gray">{t('priceTable.inList')}</Tag>}
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        )}
        {!candidates.isLoading && !candidates.error && items.length === 0 && (
          <EmptyState headingLevel={3} heading={t('admin.noResultsTitle')} description={t('admin.noResultsHint')} />
        )}
        {items.length < total && (
          <Button kind="ghost" onClick={() => setSize((s) => s + PAGE)}>
            {t('priceTable.showMore', { shown: items.length, total })}
          </Button>
        )}
        <div className="pos-price-picker__defaults">
          <NumberInput
            id="price-picker-price"
            label={mark(t('priceTable.priceForAll'), false, marking)}
            helperText={t('priceTable.priceForAllHelp')}
            min={0}
            hideSteppers
            allowEmpty
            value={price}
            onChange={(_, { value }) => setPrice(value === '' || value === undefined ? '' : String(value))}
          />
          <DateField
            id="price-picker-from"
            labelText={mark(t('priceTable.columns.from'), true, marking)}
            value={from}
            invalid={fromMissing}
            invalidText={t('admin.validation.required')}
            onChange={setFrom}
          />
        </div>
      </div>
    </FormModal>
  );
}
